export interface SheetInfo {
  file: string;
  frames: number;
  fw: number;
  fh: number;
  img: HTMLImageElement;
}

interface FactionAtlas {
  weapons: { a: SheetInfo[]; b: SheetInfo[] };
  body: SheetInfo;
  bodyHalf: SheetInfo;
  minions: SheetInfo[];
  towers: SheetInfo[];
}

interface RawSheet {
  file: string;
  frames: number;
  fw: number;
  fh: number;
}

interface RawFaction {
  weapons: { a: RawSheet[]; b: RawSheet[] };
  body: RawSheet;
  bodyHalf: RawSheet;
  minions?: RawSheet[];
  towers?: RawSheet[];
}

interface RawAtlas {
  frame: number;
  factions: Record<string, RawFaction>;
  ui: Record<string, string>;
  env: Record<string, string>;
  bosses?: Record<string, string>;
}

export class Assets {
  factions: Record<string, FactionAtlas> = {};
  ui: Record<string, HTMLImageElement> = {};
  env: Record<string, HTMLImageElement> = {};
  bosses: Record<string, SheetInfo> = {};
  loaded = false;

  async load(base: string, onProgress?: (p: number) => void): Promise<void> {
    const res = await fetch(`${base}art/atlas.json`);
    const raw = (await res.json()) as RawAtlas;
    const jobs: Array<Promise<void>> = [];
    let done = 0;
    let total = 0;
    const track = <T>(p: Promise<T>): Promise<T> => {
      total++;
      return p.then((v) => {
        done++;
        onProgress?.(done / total);
        return v;
      });
    };
    const loadImg = (file: string): Promise<HTMLImageElement> =>
      track(
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error(`Failed to load ${file}`));
          img.src = `${base}art/${file}`;
        }),
      );
    const sheet = async (r: RawSheet): Promise<SheetInfo> => ({ ...r, img: await loadImg(r.file) });

    for (const [fid, f] of Object.entries(raw.factions)) {
      jobs.push(
        (async () => {
          const [a, b, body, bodyHalf, minions, towers] = await Promise.all([
            Promise.all(f.weapons.a.map(sheet)),
            Promise.all(f.weapons.b.map(sheet)),
            sheet(f.body),
            sheet(f.bodyHalf),
            Promise.all((f.minions ?? []).map(sheet)),
            Promise.all((f.towers ?? []).map(sheet)),
          ]);
          this.factions[fid] = { weapons: { a, b }, body, bodyHalf, minions, towers };
        })(),
      );
    }
    for (const [k, file] of Object.entries(raw.ui)) jobs.push(loadImg(file).then((img) => void (this.ui[k] = img)));
    for (const [k, file] of Object.entries(raw.env)) jobs.push(loadImg(file).then((img) => void (this.env[k] = img)));
    for (const [k, file] of Object.entries(raw.bosses ?? {})) {
      jobs.push(
        loadImg(file).then((img) => {
          this.bosses[k] = { file, frames: 1, fw: img.naturalWidth, fh: img.naturalHeight, img };
        }),
      );
    }
    await Promise.all(jobs);
    this.loaded = true;
  }

  weapon(faction: string, slot: 'a' | 'b', rank: number): SheetInfo {
    const f = this.factions[faction] ?? this.factions.purple;
    const list = f.weapons[slot];
    return list[Math.min(list.length - 1, Math.max(0, rank - 1))];
  }

  body(faction: string, kind: 'body' | 'bodyHalf'): SheetInfo {
    const f = this.factions[faction] ?? this.factions.purple;
    return f[kind];
  }

  /** Portrait for a merge rank. Card level does not select a frame. Rank 5 reuses the top mark. */
  private rankSheet(list: SheetInfo[] | undefined, rank: number): SheetInfo | null {
    if (!list || list.length === 0) return null;
    return list[Math.min(list.length - 1, Math.max(0, rank - 1))];
  }

  minion(faction: string, rank: number): SheetInfo | null {
    const f = this.factions[faction] ?? this.factions.purple;
    return this.rankSheet(f.minions, rank);
  }

  tower(faction: string, rank: number): SheetInfo | null {
    const f = this.factions[faction] ?? this.factions.purple;
    return this.rankSheet(f.towers, rank);
  }

  /** A boss's own portrait. Missing ids fall back to a unit sprite. */
  boss(id: string): SheetInfo | null {
    return this.bosses[id] ?? null;
  }
}

export const assets = new Assets();

/** Draw frame `frame` of a sheet centred at (cx, cy) with `height` px tall, rotated by `rot` radians. */
export function drawSheetFrame(
  ctx: CanvasRenderingContext2D,
  s: SheetInfo,
  frame: number,
  cx: number,
  cy: number,
  height: number,
  rot = 0,
  alpha = 1,
): void {
  const f = Math.max(0, Math.min(s.frames - 1, frame | 0));
  const scale = height / s.fh;
  const w = s.fw * scale;
  ctx.save();
  ctx.translate(cx, cy);
  if (rot) ctx.rotate(rot);
  if (alpha !== 1) ctx.globalAlpha *= alpha;
  ctx.drawImage(s.img, f * s.fw, 0, s.fw, s.fh, -w / 2, -height / 2, w, height);
  ctx.restore();
}

/** Tinted white icon. */
export function drawIcon(ctx: CanvasRenderingContext2D, img: HTMLImageElement | undefined, x: number, y: number, size: number, color: string, alpha = 1): void {
  if (!img) return;
  const off = iconCache(img, color);
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.drawImage(off, x - size / 2, y - size / 2, size, size);
  ctx.restore();
}

const ICON_CACHE = new Map<string, HTMLCanvasElement>();
function iconCache(img: HTMLImageElement, color: string): HTMLCanvasElement {
  const key = `${img.src}|${color}`;
  let c = ICON_CACHE.get(key);
  if (c) return c;
  c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  const cx = c.getContext('2d')!;
  cx.drawImage(img, 0, 0);
  cx.globalCompositeOperation = 'source-in';
  cx.fillStyle = color;
  cx.fillRect(0, 0, c.width, c.height);
  ICON_CACHE.set(key, c);
  return c;
}
